const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-color-border: var(--ctl-color-border);
    --ctl-color-surface: var(--ctl-color-surface);
    --ctl-color-surface-2: var(--ctl-color-surface-2);
    --ctl-color-text: var(--ctl-color-text);
    --ctl-focus-ring: var(--ctl-focus-ring);

    --ctl-handle-size: 10px;
    --ctl-handle-hit: 16px;
    --ctl-handle-radius: 999px;

    display:block;
    min-height: 0;
    min-width: 0;
  }

  .root{
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 0;
    min-width: 0;
    display: grid;
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg, 14px);
    overflow: hidden;
  }

  /* horizontal (left/right) */
  :host([direction="horizontal"]) .root{
    grid-template-columns: var(--_a, 30%) var(--_handle, var(--ctl-handle-hit)) 1fr;
    grid-template-areas: "a handle b";
  }

  /* vertical (top/bottom) */
  :host([direction="vertical"]) .root{
    grid-template-rows: var(--_a, 30%) var(--_handle, var(--ctl-handle-hit)) 1fr;
    grid-template-areas: "a" "handle" "b";
  }

  .pane{
    min-width: 0;
    min-height: 0;
    overflow: auto;
    background: var(--ctl-color-surface);
  }

  .paneA{ grid-area: a; }
  .paneB{ grid-area: b; background: var(--ctl-color-surface-2); background: color-mix(in srgb, var(--ctl-color-surface-2) 92%, var(--ctl-color-surface)); }

  /* handle */
  .handle{
    grid-area: handle;
    position: relative;
    display:flex;
    align-items:center;
    justify-content:center;
    background: transparent;
    background: color-mix(in srgb, var(--ctl-color-border) 14%, transparent);
    cursor: col-resize;
    user-select: none;
    touch-action: none;
  }
  :host([direction="vertical"]) .handle{
    cursor: row-resize;
  }

  .grip{
    width: 40px;
    height: var(--ctl-handle-size);
    border-radius: var(--ctl-handle-radius);
    background: var(--ctl-color-border);
    background: color-mix(in srgb, var(--ctl-color-border) 70%, transparent);
  }
  :host([direction="vertical"]) .grip{
    width: var(--ctl-handle-size);
    height: 40px;
  }

  .handleBtn{
    position: absolute;
    inset: 0;
    appearance: none;
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    cursor: inherit;
  }
  .handleBtn:focus-visible{
    outline: none;
    box-shadow: var(--ctl-focus-ring);
  }

  /* collapsed */
  :host([collapsed="a"][direction="horizontal"]) .root{
    grid-template-columns: 0px var(--_handle, var(--ctl-handle-hit)) 1fr;
  }
  :host([collapsed="b"][direction="horizontal"]) .root{
    grid-template-columns: 1fr var(--_handle, var(--ctl-handle-hit)) 0px;
  }

  :host([collapsed="a"][direction="vertical"]) .root{
    grid-template-rows: 0px var(--_handle, var(--ctl-handle-hit)) 1fr;
  }
  :host([collapsed="b"][direction="vertical"]) .root{
    grid-template-rows: 1fr var(--_handle, var(--ctl-handle-hit)) 0px;
  }

  :host([collapsed="a"]) .paneA,
  :host([collapsed="b"]) .paneB{
    overflow: hidden;
  }

  /* show subtle edge line when collapsed */
  :host([collapsed="a"]) .handle,
  :host([collapsed="b"]) .handle{
    background: transparent;
    background: color-mix(in srgb, var(--ctl-color-border) 20%, transparent);
  }

  /* hide scrollbars in collapsed pane (mostly cosmetic) */
  :host([collapsed="a"]) .paneA::-webkit-scrollbar,
  :host([collapsed="b"]) .paneB::-webkit-scrollbar{
    width: 0; height: 0;
  }
</style>

<div class="root" part="root">
  <section class="pane paneA" part="pane-a"><slot name="a"></slot></section>

  <div class="handle" part="handle" aria-hidden="false">
    <button class="handleBtn" id="handleBtn" type="button"
      aria-label="Resize split pane"
      title="Drag to resize (Arrow keys adjust)"
    ></button>
    <div class="grip" part="grip" aria-hidden="true"></div>
  </div>

  <section class="pane paneB" part="pane-b"><slot name="b"></slot></section>
</div>
`;

function clampDirection(v){
  return (v === "vertical") ? "vertical" : "horizontal";
}
function clampCollapsible(v){
  return (v === "a" || v === "b" || v === "both") ? v : "none";
}
function clampCollapsed(v){
  return (v === "a" || v === "b") ? v : "none";
}
function numberOrNull(v){
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function clamp(n, min, max){
  return Math.max(min, Math.min(max, n));
}

export class CtlSplitPane extends HTMLElement {
  static get observedAttributes() {
    return ["direction","value","min-a","min-b","collapsible","collapsed"];
  }

  constructor(){
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._btn = this.shadowRoot.getElementById("handleBtn");

    this._dragging = false;
    this._start = null;

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onDblClick = this._onDblClick.bind(this);
  }

  connectedCallback(){
    if (!this.hasAttribute("direction")) this.setAttribute("direction","horizontal");
    if (!this.hasAttribute("value")) this.setAttribute("value","30");
    if (!this.hasAttribute("min-a")) this.setAttribute("min-a","240px");
    if (!this.hasAttribute("min-b")) this.setAttribute("min-b","240px");
    if (!this.hasAttribute("collapsible")) this.setAttribute("collapsible","none");
    if (!this.hasAttribute("collapsed")) this.setAttribute("collapsed","none");

    this._btn.addEventListener("pointerdown", this._onPointerDown);
    this._btn.addEventListener("keydown", this._onKeyDown);
    this._btn.addEventListener("dblclick", this._onDblClick);

    this._sync();
  }

  disconnectedCallback(){
    this._btn.removeEventListener("pointerdown", this._onPointerDown);
    this._btn.removeEventListener("keydown", this._onKeyDown);
    this._btn.removeEventListener("dblclick", this._onDblClick);
    this._teardownDrag();
  }

  attributeChangedCallback(name){
    if (name === "direction") {
      const d = clampDirection(this.getAttribute("direction"));
      if (this.getAttribute("direction") !== d) this.setAttribute("direction", d);
    }
    if (name === "collapsible") {
      const c = clampCollapsible(this.getAttribute("collapsible"));
      if (this.getAttribute("collapsible") !== c) this.setAttribute("collapsible", c);
    }
    if (name === "collapsed") {
      const c = clampCollapsed(this.getAttribute("collapsed"));
      if (this.getAttribute("collapsed") !== c) this.setAttribute("collapsed", c);
    }
    this._sync();
  }

  // ---- API ----
  get direction(){ return clampDirection(this.getAttribute("direction")); }
  set direction(v){ this.setAttribute("direction", clampDirection(v)); }

  get value(){
    const n = numberOrNull(this.getAttribute("value"));
    return n == null ? 30 : clamp(n, 0, 100);
  }
  set value(v){ this.setAttribute("value", String(numberOrNull(v) ?? 30)); }

  get minA(){ return this.getAttribute("min-a") || "240px"; }
  set minA(v){ this.setAttribute("min-a", String(v || "240px")); }

  get minB(){ return this.getAttribute("min-b") || "240px"; }
  set minB(v){ this.setAttribute("min-b", String(v || "240px")); }

  get collapsible(){ return clampCollapsible(this.getAttribute("collapsible")); }
  set collapsible(v){ this.setAttribute("collapsible", clampCollapsible(v)); }

  get collapsed(){ return clampCollapsed(this.getAttribute("collapsed")); }
  set collapsed(v){ this.setAttribute("collapsed", clampCollapsed(v)); }

  // ---- internals ----
  _sync(){
    // apply min sizes via CSS variables on host
    this.style.setProperty("--ctl-min-a", this.minA);
    this.style.setProperty("--ctl-min-b", this.minB);

    // When collapsed, don't apply percentage sizing to A (grid handles collapse)
    const collapsed = this.collapsed;
    if (collapsed === "none") {
      // set A as percentage
      this.style.setProperty("--_a", `${this.value}%`);
    } else {
      this.style.removeProperty("--_a");
    }

    // Update ARIA hints for direction
    const d = this.direction;
    this._btn.setAttribute("aria-orientation", d === "vertical" ? "vertical" : "horizontal");
  }

  _boundsFromRect(rect){
    // convert min-a/min-b lengths to px using a measurement element
    // simplest: use computed style of host with a throwaway
    const minA = this._toPx(this.minA, rect, "a");
    const minB = this._toPx(this.minB, rect, "b");

    const total = this.direction === "vertical" ? rect.height : rect.width;
    // If total is too small, fallback to 0..100
    if (!Number.isFinite(total) || total <= 0) return { minPct: 0, maxPct: 100 };

    const minPct = clamp((minA / total) * 100, 0, 100);
    const maxPct = clamp(100 - (minB / total) * 100, 0, 100);

    return { minPct, maxPct };
  }

  _toPx(value, rect){
    // supports px, %, rem, em (approx), and falls back to px if parseable
    const s = String(value || "").trim();
    if (s.endsWith("px")) return Number(s.slice(0, -2)) || 0;
    if (s.endsWith("%")) {
      const pct = Number(s.slice(0, -1)) || 0;
      const total = this.direction === "vertical" ? rect.height : rect.width;
      return (pct / 100) * total;
    }
    if (s.endsWith("rem")) {
      const n = Number(s.slice(0, -3)) || 0;
      const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return n * root;
    }
    if (s.endsWith("em")) {
      const n = Number(s.slice(0, -2)) || 0;
      const fs = parseFloat(getComputedStyle(this).fontSize) || 16;
      return n * fs;
    }
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  }

  _onPointerDown(e){
    e.preventDefault();
    this._btn.setPointerCapture?.(e.pointerId);

    const rect = this.getBoundingClientRect();
    const bounds = this._boundsFromRect(rect);

    this._dragging = true;
    this._start = {
      pointerId: e.pointerId,
      rect,
      bounds,
      startValue: this.value
    };

    window.addEventListener("pointermove", this._onPointerMove, { passive: false });
    window.addEventListener("pointerup", this._onPointerUp, { passive: false });
    window.addEventListener("pointercancel", this._onPointerUp, { passive: false });
  }

  _onPointerMove(e){
    if (!this._dragging || !this._start) return;
    e.preventDefault();

    const { rect, bounds } = this._start;

    // If currently collapsed, uncollapse on drag (if allowed)
    if (this.collapsed !== "none") {
      this.collapsed = "none";
      this.dispatchEvent(new CustomEvent("ctl-collapse", {
        bubbles: true,
        composed: true,
        detail: { collapsed: "none" }
      }));
    }

    const pos = this.direction === "vertical"
      ? (e.clientY - rect.top)
      : (e.clientX - rect.left);

    const total = this.direction === "vertical" ? rect.height : rect.width;
    if (!total) return;

    let pct = (pos / total) * 100;
    pct = clamp(pct, bounds.minPct, bounds.maxPct);

    this.value = pct;
    this.style.setProperty("--_a", `${pct}%`);

    this.dispatchEvent(new CustomEvent("ctl-resize", {
      bubbles: true,
      composed: true,
      detail: { value: this.value, direction: this.direction }
    }));
  }

  _onPointerUp(){
    this._teardownDrag();
  }

  _teardownDrag(){
    if (!this._dragging) return;
    this._dragging = false;
    this._start = null;
    window.removeEventListener("pointermove", this._onPointerMove);
    window.removeEventListener("pointerup", this._onPointerUp);
    window.removeEventListener("pointercancel", this._onPointerUp);
  }

  _onKeyDown(e){
    const step = e.shiftKey ? 5 : 1;

    // Collapse toggles (Enter/Space) if configured
    if ((e.key === "Enter" || e.key === " ") && this.collapsible !== "none") {
      e.preventDefault();
      // toggle collapse preference: collapse A first, then none, then B if both
      const cap = this.collapsible;
      const cur = this.collapsed;
      let next = "none";

      if (cap === "a") next = (cur === "a") ? "none" : "a";
      else if (cap === "b") next = (cur === "b") ? "none" : "b";
      else if (cap === "both") {
        if (cur === "none") next = "a";
        else if (cur === "a") next = "b";
        else next = "none";
      }

      this.collapsed = next;
      this.dispatchEvent(new CustomEvent("ctl-collapse", {
        bubbles: true,
        composed: true,
        detail: { collapsed: this.collapsed }
      }));
      return;
    }

    // Arrow resizing
    const horiz = this.direction === "horizontal";
    const incKeys = horiz ? ["ArrowRight"] : ["ArrowDown"];
    const decKeys = horiz ? ["ArrowLeft"] : ["ArrowUp"];

    if (incKeys.includes(e.key) || decKeys.includes(e.key)) {
      e.preventDefault();

      // If collapsed, uncollapse before resizing
      if (this.collapsed !== "none") {
        this.collapsed = "none";
        this.dispatchEvent(new CustomEvent("ctl-collapse", {
          bubbles: true,
          composed: true,
          detail: { collapsed: "none" }
        }));
      }

      const rect = this.getBoundingClientRect();
      const { minPct, maxPct } = this._boundsFromRect(rect);

      const delta = incKeys.includes(e.key) ? step : -step;
      const next = clamp(this.value + delta, minPct, maxPct);

      this.value = next;
      this.style.setProperty("--_a", `${next}%`);

      this.dispatchEvent(new CustomEvent("ctl-resize", {
        bubbles: true,
        composed: true,
        detail: { value: this.value, direction: this.direction }
      }));
    }
  }

  _onDblClick(e){
    // Double-click: quick toggle collapse if enabled
    const cap = this.collapsible;
    if (cap === "none") return;
    e.preventDefault();

    let next = "none";
    if (cap === "a") next = (this.collapsed === "a") ? "none" : "a";
    else if (cap === "b") next = (this.collapsed === "b") ? "none" : "b";
    else if (cap === "both") next = (this.collapsed === "none") ? "a" : "none";

    this.collapsed = next;
    this.dispatchEvent(new CustomEvent("ctl-collapse", {
      bubbles: true,
      composed: true,
      detail: { collapsed: this.collapsed }
    }));
  }
}

customElements.define("ctl-split-pane", CtlSplitPane);
